import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Link,
  Flex,
  Text,
  Heading,
  useToast,
} from '@chakra-ui/react';
import './LoginForm.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';

interface LoginFormProps {
  onForgotPasswordClick: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onForgotPasswordClick }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (email.trim() !== '' && password.trim() !== '') {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [email, password]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      await login(email, password);
      toast({
        title: t('auth.loginSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/');
    } catch (error) {
      toast({
        title: (error as Error).message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box textAlign="center" className="login-form">
      <Heading
        as="h1"
        size={{ base: 'md', md: 'lg' }}
        fontWeight={500}
        className="auth-title"
      >
        {t('login_Header')}
      </Heading>

      <FormControl id="email" mb={{ base: 5, md: 10 }}>
        <Input
          type="email"
          placeholder={t('username')}
          variant="outline-only-bottom"
          color="black"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>

      <FormControl id="password" mb={2}>
        <Input
          variant="outline-only-bottom"
          color="black"
          type="password"
          placeholder={t('password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </FormControl>

      <Flex justifyContent={'end'}>
        <Text fontSize={{ base: 'xs', md: 'sm' }}>
          <Link
            fontWeight={400}
            color="alea.500"
            onClick={onForgotPasswordClick}
          >
            {t('forgotPassword_Question')}
          </Link>
        </Text>
      </Flex>

      <Button
        data-testid="login-submit-button"
        colorScheme="alea"
        variant="solid"
        borderRadius={20}
        mb={4}
        mt={{ base: '30px', md: '50px' }}
        width={{ base: '100%', md: '300px' }}
        height={{ base: '45px', md: '50px' }}
        fontSize={{ base: '14px', md: '18px' }}
        fontWeight={400}
        isDisabled={isButtonDisabled}
        isLoading={isLoading}
        onClick={handleLogin}
      >
        {t('login_Button')}
      </Button>
    </Box>
  );
};

export default LoginForm;
